// Construir filas de paleta (colors, colorsWithAlpha, borderColors) desde hex
function hexToPalette(hexList, alpha = 0.6) {
  const colors = hexList.map((h) => (h.startsWith('#') ? h : `#${h}`));
  const colorsWithAlpha = colors.map((hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  });
  return { colors, colorsWithAlpha, borderColors: [...colors] };
}

// Tres paletas por defecto para el editor (clara, oscura, pastel)
// Clara: tonos claros pero diferenciados (teal, azul, slate, periwinkle)
export const DEFAULT_PALETTES = {
  light: hexToPalette([
    '#3D7B8C', '#5B8FA3', '#6B7BA4', '#4A7B96', '#7A9BB8',
    '#5A6B8A', '#5B8A9E', '#6B8EB5', '#4B6B8A', '#7B8FA8',
  ]),
  dark: hexToPalette([
    '#2C5282', '#2B6CB0', '#1A365D', '#3182CE', '#234E77',
    '#2D3748', '#2B6CB0', '#1E4D7B', '#3B5998', '#4A5568',
  ]),
  // Pastel: suave pero con más intensidad (mint, lavanda, melocotón, sage, cielo, rosa, mantequilla, ciruela, teal)
  pastel: hexToPalette([
    '#6FBFAA', '#8A9BD4', '#E5A97A', '#9BC77A', '#6BAED4',
    '#DC8E8E', '#DBC75A', '#B86AB8', '#5AB8A8', '#68C898',
  ]),
};

/** Colores de clases en carrusel / overlays: misma familia que paleta pastel de gráficos. */
export const CAROUSEL_DETECTION_COLORS = {
  person: DEFAULT_PALETTES.pastel.colors[5],
  car: DEFAULT_PALETTES.pastel.colors[4],
  default: DEFAULT_PALETTES.pastel.colors[2],
  highlight: DEFAULT_PALETTES.pastel.colors[7],
  success: DEFAULT_PALETTES.pastel.colors[3],
};

export function hexToRgba(hex, alpha = 0.6) {
  const h = hex.startsWith('#') ? hex : `#${hex}`;
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export const PALETTE_IDS = ['light', 'dark', 'pastel'];
export const PALETTE_LABELS = { light: 'Clara', dark: 'Oscura', pastel: 'Pastel' };

// Propiedades de heatmap (transparencia y colores de rango) — por paleta
export const DEFAULT_HEATMAP = {
  cellOpacity: 0.75,
  colorLow: '#0064dc',
  colorHigh: '#ff0000',
  colorOpacity: 0.7,
};

// Paleta y tipografia de VSaaS. El fondo, texto secundario, borde y
// tipografia salen de forensic-reconstruction-ui (rama main, src/styles/theme.scss),
// que es el repo de referencia de marca para VSaaS a futuro. El primary NO sale de ahi:
// forensic ya actualizo $green-base a #707EDC para su propio rediseño (aun en marcha
// blanca), pero lo que esta publicado hoy en admin.vsaas.ai sigue siendo el
// $green-base de xompass-suite, #5e6eb4 (xompass-suite/common/assets/sass/_variables.scss).
export const vsaasTheme = {
  primary: {
    main: '#5E6EB4', // xompass-suite $green-base (color real en admin.vsaas.ai hoy)
    light: '#7684BF', // main aclarado ~15% hacia blanco
    dark: '#505E99', // main oscurecido ~15% hacia negro
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#2c303b', // themeColors.secondary / $brand-primary
    light: '#4A4F5E',
    dark: '#1A1D24',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#F5F5FB', // $body-bg
    paper: '#FFFFFF',
  },
  text: {
    primary: '#000000',
    secondary: '#5B7179', // $gray-dark
  },
  // Tokens sueltos que no tienen slot propio en la paleta de MUI pero se
  // reutilizan en varios componentes (bordes sutiles de tiles, tool header).
  border: {
    light: '#e3ebee', // $gray-lighter
  },
  typography: {
    fontFamily: '"Open Sans", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  // Paleta extendida para gráficos, derivada del primary real de xompass-suite
  // (#5E6EB4) en vez del cyan del logo anterior.
  chart: {
    colors: [
      '#5E6EB4', // Periwinkle principal (primary.main)
      '#3FA21B', // Verde (themeColors.green)
      '#505E99', // Periwinkle oscuro (primary.dark)
      '#7684BF', // Periwinkle claro (primary.light)
      '#57889C', // Azul grisaceo ($brand-info)
      '#F0AD4E', // Ambar ($brand-warning)
      '#464F86', // Periwinkle muy oscuro, acento
      '#2c303b', // Gris oscuro de marca (secondary)
      '#F63C45', // Rojo (themeColors.warningRed), uso puntual
      '#00A86B', // Verde azulado ($brand-success)
    ],
    // Colores con transparencia para áreas y barras
    colorsWithAlpha: [
      'rgba(94, 110, 180, 0.6)',
      'rgba(63, 162, 27, 0.6)',
      'rgba(80, 94, 153, 0.6)',
      'rgba(118, 132, 191, 0.6)',
      'rgba(87, 136, 156, 0.6)',
      'rgba(240, 173, 78, 0.6)',
      'rgba(70, 79, 134, 0.6)',
      'rgba(44, 48, 59, 0.6)',
      'rgba(246, 60, 69, 0.6)',
      'rgba(0, 168, 107, 0.6)',
    ],
    // Colores para bordes
    borderColors: [
      '#5E6EB4',
      '#3FA21B',
      '#505E99',
      '#7684BF',
      '#57889C',
      '#F0AD4E',
      '#464F86',
      '#2c303b',
      '#F63C45',
      '#00A86B',
    ],
  },
};

export default vsaasTheme;
